import { len2 } from "../core/math.js";
import {
  BALL,
  FIELD,
  crossbarHeight,
  goalMouth,
  IMPACT,
  PLAYER,
  type Ball,
  type Player,
  type TeamSide,
} from "@golazo/shared";

export { collideBallWithPlayers } from "./goalkeeper.js";

/** Evento de choque entre jugadores (para juice / camara). */
export interface PlayerBumpEvent {
  x: number;
  y: number;
  /** 0..1 segun velocidad relativa de cierre. */
  intensity: number;
  sideline: boolean;
}

/**
 * Separa jugadores solapados e intercambia la componente normal de su
 * velocidad. Masas iguales, asi que el reparto es mitad y mitad.
 */
export function resolvePlayerCollisions(players: readonly Player[]): PlayerBumpEvent[] {
  const events: PlayerBumpEvent[] = [];

  for (let i = 0; i < players.length; i++) {
    for (let j = i + 1; j < players.length; j++) {
      const a = players[i]!;
      const b = players[j]!;

      let nx = b.pos.x - a.pos.x;
      let ny = b.pos.y - a.pos.y;
      const minDist = a.radius + b.radius;
      const distSq = nx * nx + ny * ny;
      if (distSq >= minDist * minDist) continue;

      let dist = Math.sqrt(distSq);
      if (dist < 1e-6) {
        // Centros exactamente encima: elegimos una normal arbitraria estable.
        nx = 1;
        ny = 0;
        dist = 1;
      } else {
        nx /= dist;
        ny /= dist;
      }

      const overlap = (minDist - dist) / 2;
      a.pos.x -= nx * overlap;
      a.pos.y -= ny * overlap;
      b.pos.x += nx * overlap;
      b.pos.y += ny * overlap;

      const vn = (b.vel.x - a.vel.x) * nx + (b.vel.y - a.vel.y) * ny;
      if (vn >= 0) continue; // Ya se estan separando.

      const impulse = (-(1 + PLAYER.RESTITUTION) * vn) / 2;
      a.vel.x -= impulse * nx;
      a.vel.y -= impulse * ny;
      b.vel.x += impulse * nx;
      b.vel.y += impulse * ny;

      // Choque de cuerpo fuerte: un instante de peor control para ambos, no
      // solo el rebote fisico. `vn` ya es la velocidad relativa de cierre.
      if (-vn >= IMPACT.STUMBLE_REL_SPEED) {
        a.stumbleTime = Math.max(a.stumbleTime, IMPACT.STUMBLE_TIME);
        b.stumbleTime = Math.max(b.stumbleTime, IMPACT.STUMBLE_TIME);
      }

      if (-vn >= IMPACT.SIDELINE_BUMP_MIN_SPEED) {
        const midY = (a.pos.y + b.pos.y) * 0.5;
        const sideline =
          midY <= IMPACT.SIDELINE_BAND || midY >= FIELD.HEIGHT - IMPACT.SIDELINE_BAND;
        if (sideline) {
          events.push({
            x: (a.pos.x + b.pos.x) * 0.5,
            y: midY,
            intensity: Math.min(1, -vn / 420),
            sideline: true,
          });
        }
      }
    }
  }

  return events;
}


/** Lado que ha encajado el gol, o `null` si no ha habido gol. */
/**
 * `y` y `speed` son para la red: por donde entro el balon y con cuanta fuerza.
 * Se leen AQUI porque un tick despues la simulacion ya ha parado el balon para
 * la celebracion, y entonces el disparo mas duro y el mas flojo son iguales.
 */
export type GoalEvent =
  | { conceded: TeamSide; scorer: TeamSide; y: number; speed: number }
  | null;

/** Impacto contra un palo (para sacudida / particulas). */
export interface PostHitEvent {
  x: number;
  y: number;
  speed: number;
}

export interface WallCollisionResult {
  goal: GoalEvent;
  postHit: PostHitEvent | null;
}

type GoalPost = { x: number; y: number };

interface PostCollisionHit {
  t: number;
  nx: number;
  ny: number;
}

function goalPosts(mouth: ReturnType<typeof goalMouth>): GoalPost[] {
  return [
    { x: 0, y: mouth.top },
    { x: 0, y: mouth.bottom },
    { x: FIELD.WIDTH, y: mouth.top },
    { x: FIELD.WIDTH, y: mouth.bottom },
  ];
}

function combinedPostRadius(ball: Ball): number {
  return ball.radius + BALL.POST_RADIUS;
}

/** Solapamiento estricto con el circulo del palo. */
function ballOverlapsPost(ball: Ball, post: GoalPost): boolean {
  const minDist = combinedPostRadius(ball);
  const dx = ball.pos.x - post.x;
  const dy = ball.pos.y - post.y;
  return dx * dx + dy * dy < minDist * minDist - 1e-4;
}

function ballOverlapsAnyPost(ball: Ball, posts: readonly GoalPost[]): boolean {
  return posts.some((post) => ballOverlapsPost(ball, post));
}

/**
 * Y del centro del balon en el instante en que cruza el plano de los palos.
 *
 * Mirar `ball.pos.y` al final del tick es lo que hacia injusto el gol. A
 * 900 u/s el balon avanza 15 unidades por tick y el margen de decision en el
 * palo son otras ~15: justo el tamano del error. Un disparo en diagonal
 * cruzaba por fuera del palo y se contaba dentro (o al reves) segun donde
 * cayera el muestreo, no segun por donde paso.
 *
 * `t` se recorta a [0,1]: si el balon no llego a cruzar el plano este tick, se
 * usa el extremo del segmento y el comportamiento es el de siempre.
 */
function crossingT(ball: Ball, lineX: number): number {
  const dx = ball.pos.x - ball.prevPos.x;
  if (Math.abs(dx) < 1e-6) return 1;
  return Math.min(1, Math.max(0, (lineX - ball.prevPos.x) / dx));
}

function crossingY(ball: Ball, t: number): number {
  return ball.prevPos.y + (ball.pos.y - ball.prevPos.y) * t;
}

/**
 * Altura del balon al cruzar el plano de la porteria.
 *
 * Mismo razonamiento que con la `y`, y por el mismo motivo: la altura al final
 * del tick ya no es la del cruce. En una vaselina que baja, la diferencia entre
 * las dos es de decenas de unidades.
 */
function crossingLoft(ball: Ball, t: number): number {
  return ball.prevLoft + (ball.loft - ball.prevLoft) * t;
}

type BarVerdict = "under" | "hit" | "over";

/**
 * Donde pasa el balon respecto al larguero.
 *
 * El larguero se trata como el resto de la madera: un cilindro del mismo radio
 * que los postes. Asi el marco entero (dos palos y travesano) tiene el mismo
 * grosor, y un balon que va justo al angulo se comporta igual venga por donde
 * venga.
 */
function barVerdict(loftAtLine: number, ballRadius: number): BarVerdict {
  const bar = crossbarHeight();
  const reach = ballRadius + BALL.POST_RADIUS;
  if (loftAtLine + reach <= bar) return "under";
  if (loftAtLine - reach >= bar) return "over";
  return "hit";
}

/**
 * Rebote en el larguero: sale hacia el campo y, sobre todo, BAJA.
 *
 * Se le da el mismo tacto que al poste (`POST_RESTITUTION`, empuje minimo hacia
 * el cesped) porque es la misma madera, pero anadiendo la caida vertical, que
 * es lo que hace que se lea como larguero y no como pared.
 */
function applyCrossbarBounce(ball: Ball, fieldX: 1 | -1): void {
  ball.pos.x = fieldX > 0 ? ball.radius + BALL.POST_SEPARATION : FIELD.WIDTH - ball.radius - BALL.POST_SEPARATION;

  const outward = ball.vel.x * fieldX;
  if (outward < 0) ball.vel.x = -ball.vel.x * BALL.POST_RESTITUTION;
  if (ball.vel.x * fieldX < BALL.POST_MIN_OUTWARD) {
    ball.vel.x = fieldX * BALL.POST_MIN_OUTWARD;
  }

  ball.loftVel = -Math.abs(ball.loftVel) * BALL.POST_RESTITUTION - BALL.CROSSBAR_DROP;
}

/**
 * Gol valido solo si el balon cabia entre postes AL CRUZAR y no toca ningun
 * circulo. `mouthY` es la altura en ese instante, no la del final del tick.
 */
function ballCanScore(
  ball: Ball,
  mouthY: number,
  mouth: ReturnType<typeof goalMouth>,
  posts: readonly GoalPost[],
): boolean {
  if (ballOverlapsAnyPost(ball, posts)) return false;
  const r = ball.radius;
  const postR = BALL.POST_RADIUS;
  const margin = FIELD.GOAL_MOUTH_MARGIN;
  return mouthY - r > mouth.top + postR + margin && mouthY + r < mouth.bottom - postR - margin;
}

function postIsRelevant(ball: Ball, post: GoalPost, mouth: ReturnType<typeof goalMouth>): boolean {
  const reach = combinedPostRadius(ball) + 24;
  const yLo = Math.min(ball.pos.y, ball.prevPos.y);
  const yHi = Math.max(ball.pos.y, ball.prevPos.y);
  if (yHi < mouth.top - reach || yLo > mouth.bottom + reach) return false;

  const xLo = Math.min(ball.pos.x, ball.prevPos.x);
  const xHi = Math.max(ball.pos.x, ball.prevPos.x);
  if (post.x === 0) return xLo < reach;
  return xHi > FIELD.WIDTH - reach;
}

/**
 * Colision continua circulo-circulo entre prevPos y pos. Devuelve el primer
 * contacto a lo largo del segmento, o solape estatico en t=0.
 */
function findPostCollision(ball: Ball, post: GoalPost): PostCollisionHit | null {
  const combined = combinedPostRadius(ball);
  const combinedSq = combined * combined;

  const cx = ball.pos.x - post.x;
  const cy = ball.pos.y - post.y;
  if (cx * cx + cy * cy < combinedSq - 1e-4) {
    const dist = len2(cx, cy) || combined;
    return { t: 0, nx: cx / dist, ny: cy / dist };
  }

  const p0x = ball.prevPos.x;
  const p0y = ball.prevPos.y;
  const dx = ball.pos.x - p0x;
  const dy = ball.pos.y - p0y;
  const segLenSq = dx * dx + dy * dy;
  if (segLenSq < 1e-8) return null;

  const fx = p0x - post.x;
  const fy = p0y - post.y;
  if (fx * fx + fy * fy < combinedSq - 1e-4) {
    const dist = len2(fx, fy) || combined;
    return { t: 0, nx: fx / dist, ny: fy / dist };
  }

  const a = segLenSq;
  const b = 2 * (fx * dx + fy * dy);
  const c = fx * fx + fy * fy - combinedSq;
  const disc = b * b - 4 * a * c;
  if (disc < 0) return null;

  const sqrtDisc = Math.sqrt(disc);
  let t = (-b - sqrtDisc) / (2 * a);
  if (t < 0 || t > 1) {
    t = (-b + sqrtDisc) / (2 * a);
  }
  if (t < 0 || t > 1) return null;

  const hx = p0x + dx * t;
  const hy = p0y + dy * t;
  const rx = hx - post.x;
  const ry = hy - post.y;
  const dist = len2(rx, ry) || combined;
  return { t, nx: rx / dist, ny: ry / dist };
}

function applyPostCollision(
  ball: Ball,
  post: GoalPost,
  hit: PostCollisionHit,
  speedBefore: number,
): void {
  const combined = combinedPostRadius(ball) + BALL.POST_SEPARATION;
  const fieldX = post.x <= 0 ? 1 : -1;
  let nx = hit.nx;
  let ny = hit.ny;

  // Si el contacto es casi paralelo a la linea de gol, sesga el rebote al cesped
  // para no quedar atrapado entre madera y fondo.
  if (nx * fieldX < 0.25) {
    nx = nx * 0.7 + fieldX * 0.3;
    const len = len2(nx, ny) || 1;
    nx /= len;
    ny /= len;
  }

  ball.pos.x = post.x + nx * combined;
  ball.pos.y = post.y + ny * combined;

  const vn = ball.vel.x * nx + ball.vel.y * ny;
  if (vn < 0) {
    const impulse = -(1 + BALL.POST_RESTITUTION) * vn;
    ball.vel.x += impulse * nx;
    ball.vel.y += impulse * ny;
  }

  // Garantiza que el balon salga del palo; si no, el juice se dispara cada frame.
  let outward = ball.vel.x * nx + ball.vel.y * ny;
  if (outward < BALL.POST_MIN_OUTWARD) {
    const add = BALL.POST_MIN_OUTWARD - outward;
    ball.vel.x += add * nx;
    ball.vel.y += add * ny;
    outward = BALL.POST_MIN_OUTWARD;
  }

  // Empuje ligero al campo tras un impacto fuerte (rechace jugable).
  if (speedBefore >= BALL.POST_MIN_SPEED) {
    ball.vel.x += fieldX * BALL.POST_FIELD_KICK * Math.min(1, speedBefore / 1600);
  }

  ball.spin += (ball.vel.x * ny - ball.vel.y * nx) * 0.002;
}

/**
 * Postes circulares en las esquinas de la boca. Colision continua para que
 * un misil no atraviese el palo en un solo tick.
 */
function collideBallWithPosts(ball: Ball): PostHitEvent | null {
  const mouth = goalMouth();
  const posts = goalPosts(mouth);
  let hit: PostHitEvent | null = null;

  for (let iter = 0; iter < 5; iter++) {
    let best: { post: GoalPost; hit: PostCollisionHit } | null = null;

    for (const post of posts) {
      if (!postIsRelevant(ball, post, mouth)) continue;
      const found = findPostCollision(ball, post);
      if (!found) continue;
      if (!best || found.t < best.hit.t) best = { post, hit: found };
    }

    if (!best) break;

    const speedBefore = len2(ball.vel.x, ball.vel.y);
    applyPostCollision(ball, best.post, best.hit, speedBefore);

    if (speedBefore >= BALL.POST_MIN_SPEED) {
      hit = { x: ball.pos.x, y: ball.pos.y, speed: speedBefore };
    }
  }

  return hit;
}

/**
 * Paredes del campo para el balon, con la boca de porteria abierta.
 * El balon tiene que caber entero entre los postes; un roce al palo
 * cuenta, pero no un tiro claramente fuera.
 */
export function collideBallWithWalls(ball: Ball): WallCollisionResult {
  const r = ball.radius;
  const mouth = goalMouth();
  const posts = goalPosts(mouth);
  const lineCross = r * FIELD.GOAL_LINE_CROSS;

  // Techo y suelo: siempre solidos.
  if (ball.pos.y < r) {
    ball.pos.y = r;
    ball.vel.y = -ball.vel.y * BALL.WALL_RESTITUTION;
  } else if (ball.pos.y > FIELD.HEIGHT - r) {
    ball.pos.y = FIELD.HEIGHT - r;
    ball.vel.y = -ball.vel.y * BALL.WALL_RESTITUTION;
  }

  // Postes antes que el fondo: si roza madera, el rebote manda.
  let postHit = collideBallWithPosts(ball);

  let goal: GoalEvent = null;

  // El plano de decision es el de cada porteria, asi que se evalua dentro de
  // su rama: no es lo mismo cruzar por x=0 que por x=WIDTH.
  const side: 0 | 1 | -1 = ball.pos.x < r ? 1 : ball.pos.x > FIELD.WIDTH - r ? -1 : 0;

  if (side !== 0) {
    const lineX = side > 0 ? 0 : FIELD.WIDTH;
    const t = crossingT(ball, lineX);
    const betweenPosts = ballCanScore(ball, crossingY(ball, t), mouth, posts);
    const bar = betweenPosts ? barVerdict(crossingLoft(ball, t), r) : "over";

    if (betweenPosts && bar === "under") {
      const crossed = side > 0 ? ball.pos.x + lineCross <= 0 : ball.pos.x - lineCross >= FIELD.WIDTH;
      if (crossed) {
        const y = crossingY(ball, t);
        const speed = len2(ball.vel.x, ball.vel.y);
        goal =
          side > 0
            ? { conceded: "home", scorer: "away", y, speed }
            : { conceded: "away", scorer: "home", y, speed };
      }
    } else if (betweenPosts && bar === "hit") {
      const speedBefore = len2(ball.vel.x, ball.vel.y);
      applyCrossbarBounce(ball, side);
      // Se reporta como madera: para el jugador pegarle al larguero y pegarle
      // al palo son la misma jugada, y el juego ya sabe celebrarla.
      if (!postHit && speedBefore >= BALL.POST_MIN_SPEED) {
        postHit = { x: lineX, y: crossingY(ball, t), speed: speedBefore };
      }
    } else {
      ball.pos.x = side > 0 ? r : FIELD.WIDTH - r;
      if (postHit) {
        if (ball.vel.x * side < 0) ball.vel.x = -ball.vel.x * BALL.WALL_RESTITUTION;
      } else {
        ball.vel.x = -ball.vel.x * BALL.WALL_RESTITUTION;
      }
    }
  }

  // Ultima barrera: nunca gol si el balon sigue dentro del circulo del palo.
  if (goal && ballOverlapsAnyPost(ball, posts)) goal = null;

  return { goal, postHit };
}
